import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './EmployersPage.css';

// Phase D Commit 2: Honest "Employer Access Preview" page.
// No pricing. No ID.me claims. No SDVOSB claims. No live employer features.
// All employer capabilities framed as planned / in development.

const PLANNED_CAPABILITIES = [
  'Search veteran profiles by career goals, skills, certifications, branch, and location.',
  'Review civilian translations of military experience.',
  'Filter candidates by clearance tier and remote preference.',
  'Save candidates for follow-up.',
  'Send message requests when candidate privacy settings allow.',
  'Post roles and map them to veteran backgrounds.',
  'Build shortlists for hard-to-fill roles.',
];

const WHY_DIFFERENT = [
  {
    title: 'Veteran-specific transition context',
    body: 'VetQualified is built for the gap between military service and civilian careers — not retrofitted from a generic job board.',
  },
  {
    title: 'Military experience translation',
    body: 'Members translate their MOS, AFSC, rating, or specialty into civilian-readable language so employers see capability, not jargon.',
  },
  {
    title: 'Qualification clarity',
    body: 'Profiles surface what a veteran actually did — operational scope, leadership scale, certifications — instead of a one-line title.',
  },
  {
    title: 'Privacy-first candidate profiles',
    body: 'Veterans control what is visible and to whom. Contact information and resumes are not exposed by default.',
  },
  {
    title: 'Built by a service-disabled veteran founder',
    body: 'VetQualified is being developed by a 21-year Air Force veteran who lived the transition this product is built to fix.',
  },
];

const PRODUCT_PREVIEW = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
    title: 'Candidate Search',
    body: 'Filter veteran profiles by career goal, skills, certifications, branch, location, and clearance tier.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="9" y1="13" x2="15" y2="13"/>
        <line x1="9" y1="17" x2="13" y2="17"/>
      </svg>
    ),
    title: 'Veteran Skill Translation',
    body: 'See military specialties translated into civilian job families, transferable skills, and target industries.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
      </svg>
    ),
    title: 'Saved Candidates',
    body: 'Bookmark veteran profiles for follow-up and organize shortlists for specific roles.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
      </svg>
    ),
    title: 'Message Requests',
    body: 'Reach out to candidates through privacy-respecting message requests. The veteran decides whether to share contact information.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    title: 'Role Fit Signals',
    body: 'See how a veteran’s background maps to your open role — branch, specialty, clearance, leadership scope, and skill overlap.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/>
        <rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/>
        <rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
    title: 'Employer Dashboard',
    body: 'Manage candidate searches, saved shortlists, and active outreach from one place. Designed for hiring managers and recruiters.',
  },
];

export default function EmployersPage() {
  const [formState, setFormState] = useState({ name: '', company: '', email: '', role: '', message: '', honeypot: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    const { error } = await supabase
      .from('employer_inquiries')
      .insert({
        name:     formState.name.trim(),
        company:  formState.company.trim(),
        email:    formState.email.trim(),
        role:     formState.role.trim() || null,
        message:  formState.message.trim() || null,
        honeypot: formState.honeypot || null,
      });

    if (error) {
      setSubmitError('Could not submit your interest. Please try again or email hello@vetqualified.com.');
      setSubmitting(false);
      return;
    }

    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div className="emp-root">

      {/* NAV */}
      <nav className="emp-nav">
        <Link className="emp-nav-logo" to="/"><img className="emp-brand-mark" src="/vetqualified-brand-mark.png" alt="" /><span className="emp-nav-wordmark">Vet<span>Qualified</span></span></Link>
        <div className="emp-nav-links">
          <Link to="/"          className="emp-nav-link">Home</Link>
          <Link to="/jobs"      className="emp-nav-link">Jobs</Link>
          <Link to="/employers" className="emp-nav-link active">Employers</Link>
          <Link to="/employer/auth" className="emp-nav-link">Employer Sign In</Link>
          <Link to="/dashboard" className="emp-nav-cta">Member Profile</Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="emp-hero">
        <div className="emp-hero-inner">
          <p className="emp-eyebrow">Early Access · In Development</p>
          <h1 className="emp-hero-title">
            Employer Access<br />
            <em>is being built.</em>
          </h1>
          <p className="emp-hero-sub">
            VetQualified is building a veteran-specific employer portal to help organizations
            find, understand, and engage military talent with more context than a traditional
            job board. Public employer access is not yet available.
          </p>
          <div className="emp-hero-actions">
            <a href="#early-access" className="emp-btn-primary">Request Employer Early Access</a>
            <a href="#what-we-are-building" className="emp-btn-secondary">See What We're Building</a>
          </div>
          <a href="#what-we-are-building" className="emp-scroll-indicator" aria-label="Scroll to learn more">
            <span className="emp-scroll-label">Scroll</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <polyline points="19 12 12 19 5 12"/>
            </svg>
          </a>
        </div>
      </section>

      {/* PLANNED CAPABILITIES */}
      <section className="emp-section" id="what-we-are-building">
        <div className="emp-section-inner">
          <div className="emp-section-head">
            <p className="emp-section-eyebrow">Planned Employer Capabilities</p>
            <h2 className="emp-section-title">What employers will be able to do<br />when access opens.</h2>
            <p className="emp-section-sub">
              These capabilities are being developed. None are publicly available today. Listed
              here so early-access partners know what to expect.
            </p>
          </div>
          <ul className="emp-capabilities-list">
            {PLANNED_CAPABILITIES.map((item, i) => (
              <li key={i} className="emp-capability-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PRODUCT PREVIEW CARDS */}
      <section className="emp-section">
        <div className="emp-section-inner">
          <div className="emp-section-head">
            <p className="emp-section-eyebrow">Product Preview</p>
            <h2 className="emp-section-title">A closer look at what's<br />on the roadmap.</h2>
          </div>
          <div className="emp-preview-grid">
            {PRODUCT_PREVIEW.map(item => (
              <div key={item.title} className="emp-preview-card">
                <div className="emp-preview-icon">{item.icon}</div>
                <h3 className="emp-preview-title">{item.title}</h3>
                <p className="emp-preview-body">{item.body}</p>
                <span className="emp-preview-tag">In Development</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section className="emp-section emp-why-section">
        <div className="emp-section-inner">
          <div className="emp-section-head">
            <p className="emp-section-eyebrow">Why VetQualified</p>
            <h2 className="emp-section-title">Built around military experience —<br />not bolted onto a job board.</h2>
          </div>
          <div className="emp-why-grid">
            {WHY_DIFFERENT.map(item => (
              <div key={item.title} className="emp-why-card">
                <h3 className="emp-why-title">{item.title}</h3>
                <p className="emp-why-body">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EARLY ACCESS FORM */}
      <section className="emp-section emp-contact-section" id="early-access">
        <div className="emp-section-inner">
          <div className="emp-contact-grid">
            <div className="emp-contact-left">
              <p className="emp-section-eyebrow">Early Access</p>
              <h2 className="emp-section-title">Request employer<br />early access.</h2>
              <p className="emp-contact-sub">
                Tell us about your hiring needs. As employer access opens, we'll prioritize
                organizations that help us shape the product.
              </p>
              <p className="emp-contact-note">
                Submitting this form expresses interest only. There is no charge, no account,
                and no guaranteed timeline. Pricing will be announced closer to public launch.
              </p>
            </div>

            <div className="emp-contact-right">
              {!submitted ? (
                <form className="emp-form" onSubmit={handleSubmit}>
                  <div className="emp-form-row">
                    <div className="emp-form-field">
                      <label>Your Name</label>
                      <input type="text" required placeholder="Jane Smith" value={formState.name} onChange={e => setFormState({...formState, name: e.target.value})} />
                    </div>
                    <div className="emp-form-field">
                      <label>Company</label>
                      <input type="text" required placeholder="Company name" value={formState.company} onChange={e => setFormState({...formState, company: e.target.value})} />
                    </div>
                  </div>
                  <div className="emp-form-field">
                    <label>Work Email</label>
                    <input type="email" required placeholder="you@company.com" value={formState.email} onChange={e => setFormState({...formState, email: e.target.value})} />
                  </div>
                  <div className="emp-form-field">
                    <label>Your Role</label>
                    <input type="text" placeholder="Recruiter, Hiring Manager, Founder, etc." value={formState.role} onChange={e => setFormState({...formState, role: e.target.value})} />
                  </div>
                  <div className="emp-form-field">
                    <label>Hiring Needs <span>(optional)</span></label>
                    <textarea rows={4} placeholder="What roles are you hiring for? Any clearance or specialty requirements? How many positions?" value={formState.message} onChange={e => setFormState({...formState, message: e.target.value})} />
                  </div>
                  {/* Honeypot — hidden from real users; bots fill it and get rejected by RLS */}
                  <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                    <label>Leave this field empty</label>
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formState.honeypot}
                      onChange={e => setFormState({...formState, honeypot: e.target.value})}
                    />
                  </div>
                  {submitError && (
                    <div className="emp-form-error">{submitError}</div>
                  )}
                  <button type="submit" className="emp-form-submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Interest →'}
                  </button>
                </form>
              ) : (
                <div className="emp-form-success">
                  <div className="emp-success-icon">✓</div>
                  <h3>Interest noted</h3>
                  <p>
                    Thanks, {formState.name}. Your interest has been noted for this session.
                    Once employer early access opens, we'll begin reaching out to organizations
                    that submitted interest.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="emp-footer">
        <div className="emp-footer-logo">Vet<span>Qualified</span></div>
        <div>vetqualified.com · Built by veterans. For every veteran who comes after.</div>
      </footer>

    </div>
  );
}
